import { CommonActions } from '@react-navigation/native';
import React, { ComponentProps, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, TouchableOpacity } from 'react-native';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { Member } from '@components/User/Member';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupIcon } from '@components/Web/PopupIcon';
import {
  Team,
  TeamMemberRole,
  User,
  useAssignTeamOwnerMutation,
  useLeaveTeamMutation,
  useSetPrimaryTeamMutation,
} from '@graphql/generated';
import { useClearTeamContext } from '@hooks/useClearTeamContext';
import useMe from '@hooks/useMe';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useAppNavigation } from '@navigation/useAppNavigation';
import EditTeamModal from '@screens/Teams/EditTeamModal.web';
import WebModalComponent from '@screens/Teams/WebModalComponent.web';
import { ConfirmModal } from '@src/components/Modals/ConfirmModal';
import { removeTeamFromCache } from '@utils/teams/removeTeamFromCache';

type Props = Pick<ComponentProps<typeof PopupIcon>, 'backgroundColor'> & {
  popSize?: 'big' | 'small';
  name?: string;
  id?: Team['id'];
  teamUUId?: Team['uuid'];
  canEdit?: boolean;
  isPaid?: boolean;
  openEditSubscription: () => void;
  doCancelSubscription: () => void;
};

export const TeamDetailModal = ({
  popSize,
  name,
  id,
  teamUUId,
  canEdit,
  isPaid,
  openEditSubscription: _openEditSubscription,
  doCancelSubscription,
  ...rest
}: Props) => {
  const { t } = useTranslation('shared');
  const appNavigation = useAppNavigation();
  const { me } = useMe();
  const { setEditTeamConfirmModalOpen, setEditTeamConfirmTeam } =
    useWebDrawer();

  const clearTeamContext = useClearTeamContext();

  const navigateToAllTeams = () => {
    appNavigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [
          {
            name: 'my-account',
            params: {
              screen: 'tabs',
            },
          },
        ],
      })
    );
  };

  // TA-10471 QA: navigating away is not enough — the shell header keeps the
  // left team in `selectedTeam`, and its warm cache-first getTeam entry never
  // errors, so clear that context before leaving the page.
  //
  // Called from the `leaveTeamData` effect only, which is the single success
  // signal for both leave paths (member leave, and owner-transfer -> leave).
  const afterLeaveTeam = (leftTeamId?: Team['id']) => {
    clearTeamContext(leftTeamId);
    navigateToAllTeams();
  };

  const [showModal, setShowModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showOwnerLeaveMsgBox, setShowOwnerLeaveMsgBox] = useState(false);
  const [showAssignNewOwnerBox, setShowAssignNewOwnerBox] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showAssignConfirmBox, setShowAssignConfirmBox] = useState(false);
  const [showMemberLeaveConfirmModal, setShowMemberLeaveConfirmModal] =
    useState(false);
  const [assignUser, setAssignUser] = useState<User | undefined>(undefined);
  const [setPrimaryTeam, { loading: setPrimaryTeamLoading }] =
    useSetPrimaryTeamMutation();
  const [leaveTeam, { data: leaveTeamData, loading: leaveTeamLoading }] =
    useLeaveTeamMutation({
      update(cache, _result, { variables }) {
        // TA-10471 QA: drops the team from every listTeams variant AND evicts
        // the Team entity, so no cache-first getTeam read can serve a team the
        // user has just left.
        removeTeamFromCache(cache, variables?.id);
      },
    });

  useEffect(() => {
    if (!leaveTeamData) return;
    setShowAssignConfirmBox(false);
    afterLeaveTeam(id ?? team?.id);
  }, [leaveTeamData]);
  const handleOwnerLeaveBoxVisibility = () => {
    setShowOwnerLeaveMsgBox(false);
  };
  const handleShowCreateTeamModal = () => {
    setShowCreateTeamModal(!showCreateTeamModal);
  };

  const [search, setSearch] = useState('');

  // getPublicTeam, not getTeam (TA-10850): getTeam answers "The requested team
  // could not be found" for a viewer who is not a member of the team yet.
  const { fullTeam: team } = useTeamDetailFromQuery({
    teamUUId,
  });

  // TA-10280 self-review: `team` can be truthy with `users` still
  // undefined (same transient shape that crashed MembersTab) — guard with
  // `?.` rather than destructuring off a `{ users: [] }` fallback that
  // only covers `team` itself being falsy.
  const users = team?.users ?? [];
  const { id: myId } = me || { id: '' };
  const teamMembersWithoutMe = users.filter((u) => u.id !== myId);

  const handleLeaveTeam = async () => {
    if (!id) return;
    await leaveTeam({
      variables: {
        id,
      },
    });
    setShowLeaveModal(false);
    // Context cleanup + navigation happen in the `leaveTeamData` effect, so
    // they run exactly once per leave regardless of which path got here.
  };

  const [showOnlyLeftTeamOwnerMsgBox, setShowOnlyLeftTeamOwnerMsgBox] =
    useState<boolean>(false);

  const [
    assignTeamOwner,
    { data: assignTeamOwnerData, loading: assignTeamOwnerLoading },
  ] = useAssignTeamOwnerMutation();

  useEffect(() => {
    if (!assignTeamOwnerData) return;
    const teamId = team?.id;
    if (!teamId) return;
    leaveTeam({ variables: { id: teamId } });
  }, [assignTeamOwnerData]);

  const assignNewOwner = () => {
    const teamId = team?.id;
    if (!teamId || !assignUser) return;

    assignTeamOwner({
      variables: {
        id: teamId,
        newOwnerId: assignUser.id,
      },
      refetchQueries: ['listTeams', 'listTeamMembers'],
    });
  };
  const isOwner = team?.currentUserRole === TeamMemberRole.Owner;
  const isMonthly = team?.subscriptionDetails?.subscriptionInterval === 'month';

  const openLeaveTeamModal = () => {
    if (users.length === 1) {
      setShowOnlyLeftTeamOwnerMsgBox(true);
      return;
    }
    setShowLeaveModal(true);
    if (isOwner) {
      setShowOwnerLeaveMsgBox(true);
      return;
    }
    setShowMemberLeaveConfirmModal(true);
  };

  const menuList: ComponentProps<typeof PopupIcon>['popupProps']['menuList'] = [
    ...(canEdit && isPaid && isMonthly
      ? [
          {
            iconName: 'Edit' as const,
            name: 'Edit',
            onSelect: () => {
              setEditTeamConfirmTeam(team);
              setEditTeamConfirmModalOpen(true);
            },
          },
        ]
      : []),

    ...(canEdit && isPaid && !team?.subscriptionDetails?.isSubscriptionCanceled
      ? [
          {
            iconName: 'XCircle' as const,
            isRed: true,
            name: 'Cancel',
            onSelect: () => {
              doCancelSubscription();
            },
          },
        ]
      : []),

    ...(isOwner
      ? [
          {
            iconName: 'Edit' as const,
            name: 'Edit Team Info',
            onSelect: () => {
              setShowCreateTeamModal(true);
            },
          },
        ]
      : []),
    {
      iconName: 'Star' as const,
      name: t('models:teams:primaryTeam.setTitle'),
      onSelect: () => {
        setShowModal(true);
      },
    },
    {
      iconName: 'LogOut' as const,
      isRed: true,
      name: t('shared:LeaveTask'),
      onSelect: () => openLeaveTeamModal(),
    },
  ];

  return (
    <>
      <PopupIcon
        enableHoverEffect={true}
        {...rest}
        name='MoreVertical'
        variant={popSize === 'big' ? 'l' : 's'}
        paddingVertical='xxxs'
        color='textPrimary'
        popupProps={{
          menuList,
          position: ['right', 'bottom'],
          offset: [0, 0],
        }}
      />
      {showModal && (
        <ConfirmModal
          showModal={showModal}
          isLoading={setPrimaryTeamLoading}
          onClose={() => setShowModal(false)}
          onPress={async () => {
            await setPrimaryTeam({
              variables: {
                accountId: id || '',
              },

              refetchQueries: ['listTeams'],
            });
            setShowModal(false);
          }}
          buttonText={t('shared:Set')}
          title={t('models:teams.primaryTeam.title')}
          message={t('models:teams.primaryTeam.setMessage', {
            teamName: name,
          })}
        />
      )}
      <WebModalComponent
        title={t('models:teams.assignNewOwner.title')}
        modalWidth={483}
        xVisible={showAssignConfirmBox}
        msgBody={t('models:teams.assignNewOwner.message', {
          member: assignUser?.name,
        })}
        okButtonTitle={t('models:teams.removeTeamMember.assign')}
        onCloseModal={() => setShowAssignConfirmBox(false)}
        onPressOK={assignNewOwner}
        isLoading={assignTeamOwnerLoading}
      />

      {showLeaveModal && (
        <WebModalComponent
          modalWidth={483}
          modalHeight={192}
          xVisible={showOwnerLeaveMsgBox}
          msgBody={t('models:teams.leave.message')}
          okButtonTitle={t('models:teams.removeTeamMember.assign')}
          onCloseModal={handleOwnerLeaveBoxVisibility}
          onPressOK={() => setShowAssignNewOwnerBox(true)}
        />
      )}
      {showOnlyLeftTeamOwnerMsgBox && (
        <WebModalComponent
          modalWidth={483}
          modalHeight={192}
          xVisible={true}
          msgBody="You can't leave the team as you are only owner of the team."
          okButtonTitle={t('shared:Okay')}
          onCloseModal={() => setShowOnlyLeftTeamOwnerMsgBox(false)}
          onPressOK={() => setShowOnlyLeftTeamOwnerMsgBox(false)}
        />
      )}
      {showAssignNewOwnerBox && (
        <WebModal
          accessibilityLabel={t(
            'models:profile.subscriptionPlans.assignNewOwner'
          )}
          marginTop='m'
          visible={true}
          width={504}
          height={512}
          title={t('models:profile.subscriptionPlans.assignNewOwner')}
          titleVariant='labelLarge'
          titleColor='textSecondary'
          marginHorizontal='l'
          onClose={() => {
            setShowAssignNewOwnerBox(false);
          }}>
          <Box flex={1}>
            <Box
              marginHorizontal='m'
              marginTop='m'
              flexDirection='row'
              alignItems='center'>
              <SearchInput
                onTextChanged={(t: string) => setSearch(t)}
                value={search}
              />
            </Box>
            <FlatList
              data={teamMembersWithoutMe}
              renderItem={({ item }) => (
                <TouchableOpacity
                  onPress={() => {
                    setShowLeaveModal(false);
                    setAssignUser(item);
                    setShowAssignConfirmBox(true);
                    setShowAssignNewOwnerBox(false);
                  }}>
                  <Box paddingHorizontal='m' paddingVertical='xs'>
                    <Member member={item} highlightedValue={search} />
                  </Box>
                </TouchableOpacity>
              )}
              ListHeaderComponent={<Box marginTop='xs' />}
              ListFooterComponent={<Box marginTop='m' />}
              ListEmptyComponent={
                <Box m='m'>
                  <Text textAlign='center' color='grey04'>
                    {t('models:teams.assignNewOwner.noOtherMembers')}
                  </Text>
                </Box>
              }
              keyExtractor={(item) => item.id}
            />
          </Box>
        </WebModal>
      )}
      {showCreateTeamModal && team && (
        <EditTeamModal
          team={team}
          showCreateTeamModal={showCreateTeamModal}
          setShowCreateTeamModal={handleShowCreateTeamModal}
        />
      )}
      <ConfirmModal
        showModal={showMemberLeaveConfirmModal}
        isLoading={leaveTeamLoading}
        onClose={() => {
          setShowMemberLeaveConfirmModal(false);
          setShowLeaveModal(false);
        }}
        onPress={async () => {
          await handleLeaveTeam();
          setShowMemberLeaveConfirmModal(false);
        }}
        buttonText={t('shared:confirm')}
        isAlert={true}
        title={t('models:teams.leave.leaveTeamTitle')}
        message={
          <Text variant='bodySecondary'>
            {t('models:teams.leave.leaveTeamConfirmMessage')}
          </Text>
        }
      />
    </>
  );
};
